// The organisation's business details — its legal name, PAN and registered address — on the organisation's
// settings, Money → Business details (ADR-0046; spec/13 §13). Head office invites a café with its business
// name alone, so the owner completes these here (head office may have prefilled some):
//
//   GET   /v1/organizations/{o}/business-details   settings:all
//   PATCH /v1/organizations/{o}/business-details   settings:all — only what changed is sent; audited
//
// They are what a tax invoice or a bill of supply is issued under, so until the legal name and the registered
// address are both here no venue records a GST registration; the panel says so, and says when they are
// complete. A member without `settings:all` (a manager reading the organisation's settings) is shown nothing:
// the server answers them `403`, and these are the owner's.
//
// The form is also the organisation's Setup step "Business details", in a centred dialog there (ADR-0047,
// `setup/OrganizationSetupPage.tsx`): one form, one PATCH, wherever it is filled in. Saving it refreshes Setup, whose
// step reads the record's `complete` flag.

import type { Address, OrganizationBusinessDetails, UpdateOrganizationBusinessDetails } from '@clavian/api-client';
import { countries } from '@clavian/i18n';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, FormSection, SelectField, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { fieldErrors, PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { DEFAULT_COUNTRY } from '../i18n';
import { invalidateSetup } from '../setup/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { Notice, Section } from '../ui';

export const businessDetailsKey = (organizationId: string) => ['organizations', organizationId, 'business-details'] as const;

/** The organisation's business details; `undefined` while loading, and for a member who may not read them. */
export function useBusinessDetails(organizationId: string) {
  const api = useApi();
  return useQuery<OrganizationBusinessDetails>({
    queryKey: businessDetailsKey(organizationId),
    queryFn: () => api.getOrganizationBusinessDetails({ path: { organizationId } }),
    enabled: organizationId !== '',
    retry: false,
    refetchOnWindowFocus: false,
  });
}

interface Draft {
  readonly legalName: string;
  readonly pan: string;
  readonly line1: string;
  readonly line2: string;
  readonly landmark: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
  readonly country: string;
}

type DraftErrors = Partial<Record<keyof Draft, string>>;

const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function draftOf(details: OrganizationBusinessDetails): Draft {
  const address = details.registeredAddress;
  return {
    legalName: details.legalName ?? '',
    pan: details.pan ?? '',
    line1: address?.line1 ?? '',
    line2: address?.line2 ?? '',
    landmark: address?.landmark ?? '',
    locality: address?.locality ?? '',
    region: address?.region ?? '',
    postalCode: address?.postalCode ?? '',
    country: address?.country ?? DEFAULT_COUNTRY,
  };
}

function addressOf(draft: Draft): Address | undefined {
  const parts = [draft.line1, draft.line2, draft.landmark, draft.locality, draft.region, draft.postalCode];
  if (parts.every((part) => part.trim() === '')) return undefined;
  return {
    line1: draft.line1.trim(),
    ...(draft.line2.trim() === '' ? {} : { line2: draft.line2.trim() }),
    ...(draft.landmark.trim() === '' ? {} : { landmark: draft.landmark.trim() }),
    locality: draft.locality.trim(),
    region: draft.region.trim(),
    postalCode: draft.postalCode.trim(),
    country: draft.country,
  };
}

/** What changed, as the api takes it: a field left as it was is not sent. */
export function changeOf(details: OrganizationBusinessDetails, draft: Draft): UpdateOrganizationBusinessDetails {
  const legalName = draft.legalName.trim();
  const pan = draft.pan.trim().toUpperCase();
  const address = addressOf(draft);
  return {
    ...(legalName !== '' && legalName !== (details.legalName ?? '') ? { legalName } : {}),
    ...(pan !== (details.pan ?? '') ? { pan: pan === '' ? null : pan } : {}),
    ...(address !== undefined && JSON.stringify(address) !== JSON.stringify(details.registeredAddress) ? { registeredAddress: address } : {}),
  };
}

export function BusinessDetailsGroup({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const details = useBusinessDetails(organizationId);

  if (details.data === undefined) {
    // Not the owner's to see: the panel is simply not there.
    if (details.isError && problemCode(details.error) === PROBLEM.forbidden) return null;
    return (
      <Section title={t('businessDetails.title')} tip={t('businessDetails.hint')}>
        {details.isError ? (
          <LoadFailure error={details.error} message={t('businessDetails.unavailable')} onRetry={() => void details.refetch()} />
        ) : (
          <Skeleton shape="text" lines={4} />
        )}
      </Section>
    );
  }
  // Keyed by what was read, so a save that comes back starts the form from the record as it is now.
  return (
    <Section
      title={t('businessDetails.title')}
      tip={`${t('businessDetails.hint')} ${t('businessDetails.tradeName', { name: details.data.name })}`}
      actions={
        <Badge tone={details.data.complete ? 'ok' : 'warn'} dot>
          {details.data.complete ? t('businessDetails.complete') : t('businessDetails.incomplete')}
        </Badge>
      }
    >
      <BusinessDetailsForm key={JSON.stringify(details.data)} organizationId={organizationId} details={details.data} />
    </Section>
  );
}

/**
 * The business details form, as read. On its settings panel it stays open after a save; hosted in a dialog it is
 * given `onCancel` (a Cancel beside Save) and `onSaved`, called with the record as saved.
 */
export function BusinessDetailsForm({
  organizationId,
  details,
  onSaved,
  onCancel,
}: {
  readonly organizationId: string;
  readonly details: OrganizationBusinessDetails;
  readonly onSaved?: (saved: OrganizationBusinessDetails) => void;
  readonly onCancel?: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation('console');
  const problem = useProblem();
  const api = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(() => draftOf(details));
  const [errors, setErrors] = useState<DraftErrors>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const options = useMemo(() => countries(i18n.language).map((country) => ({ value: country.code, label: country.name })), [i18n.language]);
  const save = useMutation({
    mutationFn: (body: UpdateOrganizationBusinessDetails) => api.updateOrganizationBusinessDetails({ path: { organizationId }, body }),
    onSuccess: (next) => {
      queryClient.setQueryData(businessDetailsKey(organizationId), next);
      // Setup's "Business details" step, and the nav's count of what is left, read whether they are complete.
      void invalidateSetup(queryClient);
    },
  });

  const set = (patch: Partial<Draft>) => setDraft((previous) => ({ ...previous, ...patch }));
  const label = (field: string) => t(`venueProfile.label.${field}`);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFailure(undefined);
    const next: DraftErrors = {};
    if (draft.legalName.trim() === '' && details.legalName !== null) next.legalName = t('businessDetails.error.keepLegalName');
    const pan = draft.pan.trim().toUpperCase();
    if (pan !== '' && !PAN.test(pan)) next.pan = t('businessDetails.error.pan');
    const address = addressOf(draft);
    if (address !== undefined) {
      for (const field of ['line1', 'locality', 'region', 'postalCode'] as const) {
        if (draft[field].trim() === '') next[field] = t('businessDetails.error.required');
      }
    } else if (details.registeredAddress !== null) {
      next.line1 = t('businessDetails.error.keepAddress');
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const change = changeOf(details, draft);
    if (Object.keys(change).length === 0) {
      setFailure(t('businessDetails.unchanged'));
      return;
    }
    save.mutate(change, {
      onSuccess: (next) => {
        toast.show({ tone: 'ok', title: t('businessDetails.saved') });
        onSaved?.(next);
      },
      onError: (error) => {
        const placed: DraftErrors = {};
        for (const field of fieldErrors(error)) {
          const name = field.field.replace(/^registeredAddress\./, '') as keyof Draft;
          if (name in draft) placed[name] = field.message;
        }
        if (Object.keys(placed).length > 0) setErrors(placed);
        else setFailure(problem.text(error, { fallback: t('businessDetails.failed') }));
      },
    });
  }

  // What a GST document needs is not marked optional; what it can do without is.
  const text = (field: keyof Draft, fieldLabel: string, extra: { description?: string; optional?: boolean; autoCapitalize?: string } = {}) => (
    <TextField
      label={fieldLabel}
      {...(extra.description === undefined ? {} : { description: extra.description })}
      {...(extra.autoCapitalize === undefined ? {} : { autoCapitalize: extra.autoCapitalize })}
      showOptional={extra.optional === true}
      value={draft[field]}
      onChange={(event) => set({ [field]: event.target.value } as Partial<Draft>)}
      error={errors[field]}
    />
  );

  return (
    <form className="console-form" aria-label={t('businessDetails.title')} noValidate onSubmit={submit}>
      {details.complete ? null : (
        <Notice tone="warn" role="note">
          {t('businessDetails.needed')}
        </Notice>
      )}
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      {/* Six fields: the legal name and the address a GST document needs. The PAN, a second address line and a landmark wait behind More options. */}
      <FormSection
        title={t('businessDetails.field.registeredAddress')}
        className="console-business-address"
        forceOpen={errors.pan !== undefined || errors.line2 !== undefined || errors.landmark !== undefined}
        more={
          <>
            {text('pan', t('businessDetails.field.pan'), { description: t('businessDetails.help.pan'), autoCapitalize: 'characters', optional: true })}
            {text('line2', label('line2'), { optional: true })}
            {text('landmark', label('landmark'), { optional: true })}
          </>
        }
      >
        {text('legalName', t('businessDetails.field.legalName'))}
        {text('line1', label('line1'))}
        {text('locality', label('locality'))}
        {text('region', label('region'))}
        {text('postalCode', label('postalCode'))}
        <SelectField label={label('country')} options={options} value={draft.country} onValueChange={(country) => set({ country })} />
      </FormSection>
      <div className={onCancel === undefined ? 'console-form__actions' : 'console-form__actions console-form__actions--end'}>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" variant="primary" busy={save.isPending}>
          {t('businessDetails.save')}
        </Button>
      </div>
    </form>
  );
}
